import React, {FC, lazy, Suspense, useEffect, useRef} from 'react'
import noop from 'lodash/noop'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import {
  StatesButton,
  StatesButtonStates,
  TextField,
  Text,
  Checkbox,
  TextFieldTheme,
  ThreeDotsLoader,
} from 'wix-ui-tpa/cssVars'
import {useSettings, useStyles} from '@wix/tpa-settings/react'
import {Member} from '@wix/ambassador-members-v1-member/types'
import {isLastNameFirst} from '@wix/table-reservations-lib/locale'

import settingsParams from '../../settingsParams'
import stylesParams from '../../stylesParams'
import {RequestStatus} from '../../../../utils/wrapRequest'
import {PhoneInput} from '../../../../components-shared/PhoneInput'
import {FormError} from '../../../../components-shared/FormError'
import {LayoutSize} from '../../../../components-shared/LayoutSizeProvider/types'
import {PaymentTokenization} from '../PaymentTokenization'
import {useGlobalButtonStyle} from '../../../../utils/useGlobalButtonStyle'
import {globalButtonStyleParams} from '../../../../components-shared/Settings/Button/stylesParams'

import {useHooks} from './useHooks'
import {classes, st} from './Form.st.css'
import {PolicyLinks} from './PolicyLinks'
import {CancellationPolicyDisclosure} from './CancellationPolicyDisclosure'
import {
  CUSTOM_FIELD_LENGTH_MAX,
  EMAIL_LENGTH_MAX,
  FIRST_NAME_LENGTH_MAX,
  LAST_NAME_LENGTH_MAX,
  reservationDetailsFormDataHooks,
} from './constants'

const ExpressCheckout = lazy(
  () => import(/* webpackChunkName: "ReservationDetailsExpressCheckout" */ '../ExpressCheckout'),
)

interface FormProps {
  layoutSize: LayoutSize
  currentMember?: Member
  showAutoApproveButtonLabel?: boolean
}

export const Form: FC<FormProps> = ({layoutSize, currentMember, showAutoApproveButtonLabel}) => {
  const {t, i18n} = useTranslation()
  const settings = useSettings()
  const styles = useStyles()
  const globalButtonStyle = useGlobalButtonStyle(globalButtonStyleParams)
  const {isEditor} = useEnvironment()
  const firstInputRef = useRef<HTMLInputElement>()
  const isAccordionLayout = layoutSize === LayoutSize.XS || layoutSize === LayoutSize.S

  useEffect(() => {
    if (firstInputRef.current && !isEditor) {
      firstInputRef.current.focus()
    }
  }, [])

  const {
    currentReservationLocation,
    form,
    isManualApproval,
    submitReservationStatus,
    marketingCheckbox,
    isFormEdited,
    isPaymentRuleActive,
    totalPrice,
    paymentCurrency,
    isExpressCheckoutAvailable,
    canStartExpressPayment,
    areReserveeFieldsDisabled,
    paymentTokenizationSupported,
    isCancellationPolicyActive,
    cancellationPolicy,
    isCancellationFeeEnabled,
    reservationFormSettings,
    setFormBasicValues,
    handleMarketingCheckboxChange,
    handleTextFieldChange,
    handlePhoneNumberChange,
    handleCustomFieldChange,
    handleFormSubmit,
  } = useHooks()

  useEffect(() => {
    if (currentMember && !isFormEdited) {
      setFormBasicValues({
        firstName: currentMember.contact?.firstName || '',
        lastName: currentMember.contact?.lastName || '',
        email: currentMember?.loginEmail || '',
        phoneNumber: currentMember.contact?.phones?.[0] || '',
      })
    }
  }, [currentMember, isFormEdited])

  const onSubmit = async (e: React.FormEvent) => {
    await handleFormSubmit(e)

    const possibleNotValidInput = document.querySelector<HTMLInputElement>(
      `.${classes.textFieldError} input`,
    )

    if (possibleNotValidInput) {
      possibleNotValidInput.focus()
    }
  }

  const shouldShowPaymentTokenization =
    isPaymentRuleActive && isManualApproval && paymentTokenizationSupported

  const isSubmittingReservation = submitReservationStatus === RequestStatus.LOADING

  const textFieldTheme = settings.get(settingsParams.textFieldTheme) as TextFieldTheme

  const textFieldLayoutClass =
    textFieldTheme === TextFieldTheme.Box ? classes.boxFormFieldLayout : classes.lineFormFieldLayout
  const textFieldClass =
    textFieldTheme === TextFieldTheme.Box ? classes.boxFormField : classes.lineFormField

  const formRootClass = textFieldTheme === TextFieldTheme.Box ? classes.boxForm : classes.lineForm

  const formTitleTag =
    styles.get(stylesParams.subtitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.subtitleFont).htmlTag

  const completeReservationLabelText = settings.get(
    showAutoApproveButtonLabel
      ? settingsParams.buttonLabelText
      : settingsParams.buttonLabelRequestText,
  )

  const buttonLabelText =
    isPaymentRuleActive && !isManualApproval
      ? t('uou-reservations.reservation-details.continue-to-payment.cta')
      : completeReservationLabelText

  const FirstName = (
    <div className={textFieldLayoutClass}>
      <TextField
        inputRef={(input) => (firstInputRef.current = input)}
        className={st(textFieldClass, !form.firstName.valid ? classes.textFieldError : undefined)}
        label={`${t('uou-reservations.reservation-details.firstname')}`}
        maxLength={FIRST_NAME_LENGTH_MAX}
        name="firstName"
        value={form.firstName.value}
        required={form.firstName.isRequired}
        onChange={handleTextFieldChange}
        disabled={areReserveeFieldsDisabled}
        theme={textFieldTheme}
        data-hook={reservationDetailsFormDataHooks.firstName()}
      />
      {!form.firstName.valid && (
        <Error text={t('uou-reservations.reservation-details.firstname.error')} />
      )}
    </div>
  )

  const LastName = (
    <div className={textFieldLayoutClass}>
      <TextField
        className={st(textFieldClass, !form.lastName.valid ? classes.textFieldError : undefined)}
        label={`${t('uou-reservations.reservation-details.lastname')}`}
        maxLength={LAST_NAME_LENGTH_MAX}
        name="lastName"
        value={form.lastName.value}
        required={form.lastName.isRequired}
        onChange={handleTextFieldChange}
        disabled={areReserveeFieldsDisabled}
        theme={textFieldTheme}
        data-hook={reservationDetailsFormDataHooks.lastName()}
      />
      {!form.lastName.valid && (
        <Error text={t('uou-reservations.reservation-details.lastname.error')} />
      )}
    </div>
  )

  return (
    <section
      id="tr-form"
      className={st(classes.root, {[layoutSize]: true}, formRootClass)}
      data-hook={reservationDetailsFormDataHooks.root()}
    >
      <Text className={classes.formTitle} tagName={formTitleTag}>
        {t('uou-reservations.reservation-details.details.text')}
      </Text>

      <form className={classes.formBody} onSubmit={onSubmit} noValidate>
        <div className={classes.textFieldsSection}>
          {isLastNameFirst(i18n.language) ? (
            <>
              {LastName}
              {FirstName}
            </>
          ) : (
            <>
              {FirstName}
              {LastName}
            </>
          )}

          <div className={textFieldLayoutClass}>
            <div>
              <PhoneInput
                label={`${t('uou-reservations.reservation-details.phone')}*`}
                value={form.phoneNumber.value}
                onChange={handlePhoneNumberChange}
                disabled={areReserveeFieldsDisabled}
                wrapperClassName={classes.phoneWrapper}
                labelClassName={classes.phoneInputLabel}
                className={textFieldClass}
                invalid={!form.phoneNumber.valid}
                textFieldTheme={textFieldTheme}
                defaultCountry={currentReservationLocation?.location?.address?.country!}
                dataHook={reservationDetailsFormDataHooks.phoneNumber()}
              />

              {!form.phoneNumber.valid && (
                <Error text={t('uou-reservations.reservation-details.phone.error')} />
              )}
            </div>
          </div>

          <div className={textFieldLayoutClass}>
            <TextField
              className={st(textFieldClass, !form.email.valid ? classes.textFieldError : undefined)}
              label={`${t('uou-reservations.reservation-details.email')}`}
              maxLength={EMAIL_LENGTH_MAX}
              name="email"
              type="email"
              value={form.email.value}
              required={form.email.isRequired}
              onChange={handleTextFieldChange}
              disabled={areReserveeFieldsDisabled}
              theme={textFieldTheme}
              data-hook={reservationDetailsFormDataHooks.email()}
            />
            {!form.email.valid && (
              <Error text={t('uou-reservations.reservation-details.email.error')} />
            )}
          </div>

          {reservationFormSettings?.customFieldDefinitions?.map(({id, name}) => (
            <div className={st(textFieldLayoutClass, classes.customFieldLayout)} key={id}>
              <TextField
                className={st(
                  textFieldClass,
                  !form.customFields[id!]?.valid ? classes.textFieldError : undefined,
                )}
                label={`${name}`}
                maxLength={CUSTOM_FIELD_LENGTH_MAX}
                name={id!}
                value={form.customFields[id!]?.value}
                required={form.customFields[id!]?.isRequired}
                onChange={handleCustomFieldChange}
                disabled={areReserveeFieldsDisabled}
                theme={textFieldTheme}
                autoComplete="nope"
              />
              {!form.customFields[id!]?.valid && (
                <Error text={t('uou-reservations.reservation-details.custom.error')} />
              )}
            </div>
          ))}
        </div>

        {reservationFormSettings?.submitMessage && (
          <Text className={st(classes.formMessage, classes.submitMessage)} tagName="p">
            {reservationFormSettings.submitMessage}
          </Text>
        )}

        {reservationFormSettings?.emailMarketingCheckbox?.enabled && (
          <Checkbox
            className={classes.marketingCheckbox}
            checked={marketingCheckbox}
            label={t('uou-reservations.reservation-details.marketing.checkbox')}
            onChange={handleMarketingCheckboxChange}
            disabled={areReserveeFieldsDisabled}
          />
        )}

        <PolicyLinks reservationFormSettings={reservationFormSettings} />

        <CancellationPolicyDisclosure
          cancellationPolicy={cancellationPolicy}
          isCancellationPolicyActive={isCancellationPolicyActive}
          isCancellationFeeEnabled={isCancellationFeeEnabled}
        />

        {isAccordionLayout && shouldShowPaymentTokenization ? (
          <div className={classes.paymentTokenization}>
            <PaymentTokenization guestPolicy={cancellationPolicy?.guestPolicy} />
          </div>
        ) : null}

        <StatesButton
          type="submit"
          disabled={isSubmittingReservation}
          state={isSubmittingReservation ? StatesButtonStates.IN_PROGRESS : StatesButtonStates.IDLE}
          inProgressContent={<ThreeDotsLoader className={classes.loader} />}
          idleContent={buttonLabelText}
          className={classes.submitButton}
          style={globalButtonStyle}
          data-hook={reservationDetailsFormDataHooks.completeReservation()}
          // glitch in WUT. It always takes onClick as a prop despite type="submit" on button
          onClick={noop}
        />
      </form>

      {isExpressCheckoutAvailable && paymentCurrency ? (
        <Suspense fallback={null}>
          <ExpressCheckout
            currency={paymentCurrency}
            totalPrice={totalPrice}
            canStartPayment={canStartExpressPayment}
          />
        </Suspense>
      ) : null}
    </section>
  )
}

export const Error: FC<{text: string}> = ({text}) => {
  const settings = useSettings()

  const textFieldTheme = settings.get(settingsParams.textFieldTheme) as TextFieldTheme
  const errorClass =
    textFieldTheme === TextFieldTheme.Box ? classes.boxFormError : classes.lineFormError

  return <FormError text={text} className={errorClass} />
}
